import React, { useEffect, useState } from 'react';
import { publishBusiness, shareBusiness, getBusinessAnalytics } from './MiniERPPriceFlowAPI';

const PUBLIC_ORIGIN = 'https://think5whys.in';

export default function MiniERPPriceFlowPublishPage({ business, onBack, onPublished }) {
  const [analytics, setAnalytics] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const link = `${PUBLIC_ORIGIN}/p/${business.slug}`;

  const loadAnalytics = async () => {
    try {
      const data = await getBusinessAnalytics(business._id);
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => { loadAnalytics(); }, [business._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const handlePublish = async () => {
    setPublishing(true);
    setError('');
    try {
      const updated = await publishBusiness(business._id);
      onPublished && onPublished(updated);
      showToast(`Published — now live as version ${updated.version}`);
      loadAnalytics();
    } catch (err) {
      setError(err.message);
    } finally {
      setPublishing(false);
    }
  };

  const handleShare = async (channel) => {
    try {
      await shareBusiness(business._id);
    } catch { /* non-blocking — still let them share even if the counter call fails */ }

    if (channel === 'copy') {
      await navigator.clipboard.writeText(link);
      showToast('Link copied!');
    } else if (channel === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(`Check out our latest price list: ${link}`)}`, '_blank');
    } else if (channel === 'sms') {
      window.open(`sms:?body=${encodeURIComponent(`Check out our latest price list: ${link}`)}`, '_blank');
    } else if (channel === 'email') {
      window.open(`mailto:?subject=${encodeURIComponent('Our Latest Price List')}&body=${encodeURIComponent(link)}`, '_blank');
    }
    loadAnalytics();
  };

  return (
    <div className="min-h-screen bg-[#0B0B12] text-white px-4 py-6 sm:px-8 sm:py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-sm text-white/50 hover:text-white transition">← Back</button>
          <h2 className="text-xl font-bold tracking-tight">Publish &amp; Share</h2>
          <span className="w-12" />
        </div>

        {toast && (
          <div className="mb-4 px-4 py-2.5 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-300 text-sm text-center">
            {toast}
          </div>
        )}
        {error && <p className="text-rose-400 text-sm mb-4 text-center">{error}</p>}

        {/* Signature: live pulse + version */}
        <div className="rounded-3xl bg-[#14141C] ring-1 ring-white/10 p-6 mb-5 text-center">
          <div className="inline-flex items-center gap-2 mb-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400">Live</span>
          </div>
          <p className="text-3xl font-extrabold tracking-tight">Version {business.version}</p>
          <p className="text-xs text-white/40 mt-1">
            Last updated {business.lastUpdated ? new Date(business.lastUpdated).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
          </p>

          <button
            onClick={handlePublish}
            disabled={publishing}
            className="mt-5 w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-emerald-500
                       hover:from-cyan-400 hover:to-emerald-400 shadow-lg shadow-emerald-900/30 disabled:opacity-50 transition"
          >
            {publishing ? 'Publishing…' : `Publish New Version →`}
          </button>
        </div>

        {/* Share */}
        <div className="rounded-3xl bg-[#14141C] ring-1 ring-white/10 p-6 mb-5">
          <p className="text-sm font-semibold mb-3">Your permanent link</p>
          <div className="flex items-center gap-2 bg-black/30 ring-1 ring-white/10 rounded-xl px-3 py-2.5 mb-4">
            <span className="text-sm text-white/70 truncate flex-1">{link}</span>
            <button onClick={() => handleShare('copy')} className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 whitespace-nowrap">Copy</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => handleShare('whatsapp')} className="py-2.5 rounded-xl bg-white/5 ring-1 ring-white/10 text-xs font-semibold hover:bg-white/10 transition">WhatsApp</button>
            <button onClick={() => handleShare('sms')} className="py-2.5 rounded-xl bg-white/5 ring-1 ring-white/10 text-xs font-semibold hover:bg-white/10 transition">SMS</button>
            <button onClick={() => handleShare('email')} className="py-2.5 rounded-xl bg-white/5 ring-1 ring-white/10 text-xs font-semibold hover:bg-white/10 transition">Email</button>
          </div>
        </div>

        {/* Lightweight analytics — counters only, no dashboards/charts */}
        {analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Views" value={analytics.views} />
            <Stat label="Shares" value={analytics.shares} />
            <Stat label="Products" value={analytics.products} />
            <Stat label="Offers" value={analytics.offers} />
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-[#14141C] ring-1 ring-white/10 p-4 text-center">
      <p className="text-2xl font-extrabold tracking-tight text-emerald-400">{value ?? '—'}</p>
      <p className="text-[11px] uppercase tracking-widest text-white/40 mt-1">{label}</p>
    </div>
  );
}
